<script setup>
import { defineProps, defineEmits, ref } from 'vue'

const emit = defineEmits(['value'])

const props = defineProps({
  name: String,
  description: String,
  required: Boolean,
  value: Array,           // Array of strings (IPs)
  placeholder: String,
  itemType: {             // 'ip' for IP validation
    type: String,
    default: 'string'
  }
})

const inputValue = ref('')
const errorMessage = ref('')

// IP validation regex (supports IPv4)
function isValidIP(ip) {
  const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  return ipv4Regex.test(ip.trim())
}

function addItem() {
  const trimmedValue = inputValue.value.trim()

  if (!trimmedValue) {
    errorMessage.value = 'Please enter a value'
    return
  }

  // Validate IP if itemType is 'ip'
  if (props.itemType === 'ip' && !isValidIP(trimmedValue)) {
    errorMessage.value = 'Invalid IP address format'
    return
  }

  // Check for duplicates
  if (props.value.includes(trimmedValue)) {
    errorMessage.value = 'This value has already been added'
    return
  }

  const newValue = [...props.value, trimmedValue]
  emit('value', newValue)

  // Clear input and error
  inputValue.value = ''
  errorMessage.value = ''
}

function removeItem(index) {
  const newValue = [...props.value]
  newValue.splice(index, 1)
  emit('value', newValue)
}

function handleKeydown(event) {
  if (event.key === 'Enter') {
    event.preventDefault()
    addItem()
  }
}

function clearError() {
  errorMessage.value = ''
}
</script>

<template>
  <div>
    <div class="param-container">
      <div class="param-header">
        <span class="me-2 param-name">{{ props.name }}</span>
        <span class="type-badge">array</span>
        <span v-if="props.required" class="ms-2 required-badge">required</span>
      </div>
      <p class="param-description">{{ props.description }}</p>

      <!-- Input field for adding items -->
      <div class="input-group">
        <input
          v-model="inputValue"
          @keydown="handleKeydown"
          @input="clearError"
          type="text"
          class="item-input"
          :class="{ 'has-error': errorMessage }"
          :placeholder="props.placeholder || 'Enter value and press Add'"
        />
        <button @click="addItem" class="btn-add" type="button">
          <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
          Add
        </button>
      </div>

      <!-- Error message -->
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

      <!-- List of added items -->
      <div v-if="props.value.length > 0" class="items-list">
        <div
          v-for="(item, index) in props.value"
          :key="index"
          class="item-tag"
        >
          <span class="item-value">{{ item }}</span>
          <button @click="removeItem(index)" class="btn-remove" type="button">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        No items added yet
      </div>
    </div>
  </div>
</template>

<style scoped>
.param-container {
  padding: 1rem;
  border-radius: var(--radius-lg);
  background-color: var(--surface-sunken);
}

.param-header {
  display: flex;
  align-items: center;
  font-weight: normal;
  margin-bottom: 0.5rem;
}

.param-name {
  color: var(--text-primary);
  font-weight: 600;
  font-family: var(--font-heading);
  font-size: 0.95rem;
}

.type-badge {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--accent-text);
  background: var(--accent-soft-2);
  padding: 0.1rem 0.5rem;
  border-radius: var(--radius-xs);
}

.required-badge {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--color-error-text);
  background: var(--color-error-soft);
  padding: 0.1rem 0.5rem;
  border-radius: var(--radius-xs);
}

.param-description {
  color: var(--text-secondary);
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.input-group {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.item-input {
  flex: 1;
  padding: 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-strong);
  background-color: var(--surface-inset);
  color: var(--text-heading);
  font-size: 0.9rem;
  font-family: var(--font-heading);
  transition: var(--transition);
}

.item-input:focus {
  outline: none;
  border-color: var(--accent-text);
  box-shadow: 0 0 0 3px var(--accent-soft-3);
}

.item-input.has-error {
  border-color: var(--color-error);
}

.item-input::placeholder {
  color: var(--text-secondary);
}

.btn-add {
  padding: 0.75rem 1.25rem;
  background-color: var(--surface-control);
  color: var(--text-primary);
  border: none;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.btn-add:hover {
  background-color: var(--surface-control);
}

.btn-add:active {
  transform: scale(0.98);
}

.error-message {
  color: var(--color-error);
  font-size: 0.85rem;
  margin: 0.25rem 0 0.75rem 0;
}

.items-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
}

.item-tag {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: var(--surface-overlay);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  color: var(--text-primary);
  font-family: var(--font-heading);
}

.item-value {
  user-select: all;
}

.btn-remove {
  background: none;
  border: none;
  color: var(--text-faint);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: var(--radius-pill);
  transition: var(--transition);
}

.btn-remove:hover {
  background-color: var(--surface-overlay-3);
  color: var(--text-primary);
}

.empty-state {
  margin-top: 1rem;
  padding: 1rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
  border-top: 1px solid var(--border);
}
</style>
